// The question a modal asks in its own footer before a take-back (DD-045), never in a second modal. On a phone, and
// in a size sm modal at every width (`stacked`), the sentence takes its own line, the answers below it. Entering the
// step, focus moves to Keep it, the safe answer, and the sentence is announced and describes both answers; leaving it
// without acting, focus goes back once to the action that asked (useFooterStep). A question that needs an answer first
// (a work write-off's reason) takes a field on its own line under it, and the confirm waits for it.
// It sits beside ConfirmDialog because DD-045 holds for every line-item modal, not finance alone.
import { ReactNode, useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/Button";

interface Props {
  /** The question, e.g. "Delete Hosting? No expense or bill uses it." */
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onKeep: () => void;
  /** Any of the modal's requests in flight: neither answer goes out meanwhile. */
  pending: boolean;
  /** The confirm's own request in flight: its spinner. */
  loading: boolean;
  /**
   * The question on its own line at every width, for a size sm modal: the `sm:` breakpoint follows the viewport, not
   * the modal, so a narrow modal on a laptop would squeeze the question beside the answers.
   */
  stacked?: boolean;
  /** The safe answer, "Keep it" unless the question asks something else ("Keep editing"). */
  keepLabel?: string;
  /**
   * What the confirm needs answered first, e.g. why a work write-off (TD-073): on its own line under the question,
   * outside the announced sentence. The question then takes its own line too.
   */
  field?: ReactNode;
  /** The confirm waits, e.g. for `field` to be answered; Keep it never does. */
  confirmDisabled?: boolean;
}

export function FooterConfirm({
  children, confirmLabel, onConfirm, onKeep, pending, loading, stacked = false, keepLabel = "Keep it", field, confirmDisabled = false,
}: Props) {
  const id = useId();
  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-3">
      <span id={id} aria-live="polite" className={`${stacked || field ? "w-full" : "w-full sm:w-auto sm:flex-1"} min-w-0 text-sm text-gray-700`}>{children}</span>
      {field && <div className="w-full">{field}</div>}
      {/* autoFocus: the step replaced the button that had focus. */}
      <Button variant="secondary" className="shrink-0 whitespace-nowrap" onClick={onKeep} disabled={pending} aria-describedby={id} autoFocus>{keepLabel}</Button>
      <Button variant="destructive" className="shrink-0 whitespace-nowrap" onClick={onConfirm} disabled={pending || confirmDisabled} isLoading={loading} aria-describedby={id}>
        {confirmLabel}
      </Button>
    </div>
  );
}

export interface FooterStep<S extends string> {
  /** The step the footer asks about, or null for the modal's own footer. */
  step: S | null;
  ask: (step: S) => void;
  /** Back to the modal's footer without acting: Keep it, or a refusal. */
  leave: () => void;
  /** The autoFocus of the action that asks `step`: true as the footer comes back from that step's question. */
  returnsTo: (step: S) => boolean;
  /**
   * For an action that stays mounted while its question shows (a row in a list, where autoFocus never fires again):
   * a ref that focuses it as the footer comes back from its step.
   */
  backRef: (step: S) => (el: HTMLElement | null) => void;
}

/** A step held outside the hook, such as a URL param a row menu opens the modal at: what it is, and how it moves. */
export interface HeldStep<S extends string> {
  step: S | null;
  go: (step: S | null) => void;
}

/**
 * A footer's confirm step, and where focus goes back to when the step closes without acting. The step lives in the
 * hook's state from `initial`, or, given `held`, wherever that keeps it.
 */
export function useFooterStep<S extends string>(initial: S | null, held?: HeldStep<S>): FooterStep<S> {
  const [own, setOwn] = useState<S | null>(initial);
  const [left, setLeft] = useState<S | null>(null);
  const step = held ? held.step : own;
  const move = held ? held.go : setOwn;
  // Focus goes back once: autoFocus fires as the footer commits, before this effect, so clearing it here keeps an
  // action that a refetch takes away and gives back later from taking focus again mid-edit. It clears only once the
  // footer is off the step it left: a held step (React Router commits a URL in a later transition) goes a render after
  // `left` is set, and the action mounts only then.
  useEffect(() => {
    if (left !== null && step !== left) setLeft(null);
  }, [left, step]);
  return {
    step,
    ask: (next) => {
      setLeft(null);
      move(next);
    },
    leave: () => {
      setLeft(step);
      move(null);
    },
    returnsTo: (s) => left === s,
    // A new callback each render, so React calls it again on the render after leave(), the one where left is set.
    backRef: (s) => (el) => {
      if (el && left === s) el.focus();
    },
  };
}
